import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { type FC, type SyntheticEvent, useRef, useState } from "react";

import Arrow from "@/assets/images/Arrow";
import { CustomBadge, Play } from "@/components/atoms";
import { cn } from "@/lib/utils";
import { commonIconProps12 } from "@/utils/constants";

import type { MediaFile, MediaStore } from "./mediaStore";

interface UploadedFilesProps {
  media: MediaFile;
  index: number;
  mediaStore: MediaStore;
  classees?: string;
}

const UploadedFiles: FC<UploadedFilesProps> = observer(
  ({ media, index, mediaStore, classees }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlayClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
          setIsPlaying(true);
        } else {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      }
    };

    const handleRemoveMediaFile = (e: SyntheticEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      mediaStore.removeMediaFile(index);
    };

    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: media.id!,
      });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <>
        <div
          className={cn(
            "relative group w-fit h-fit! shrink-0",
            media.type.startsWith("video") && "h-[106px]!"
          )}
          ref={setNodeRef}
          style={style}
        >
          {media.type.startsWith("image") ? (
            <img
              src={media.previewUrl}
              alt="preview"
              className={cn(
                "rounded-md object-cover",
                !classees && "w-[106px] h-[106px]",
                classees
              )}
              data-testid={`uploaded-file-image-${index}`}
            />
          ) : (
            <video
              ref={videoRef}
              src={media.previewUrl}
              controls
              className="w-[106px] h-[106px] rounded-md object-cover"
              data-testid={`uploaded-file-video-${index}`}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            />
          )}

          <button
            onClick={handleRemoveMediaFile}
            className="absolute top-[-9px] right-[-4px] bg-[#030712] text-white p-1 rounded-full hover:bg-black cursor-pointer z-20"
            data-testid={`uploaded-file-remove-${index}`}
          >
            <X {...commonIconProps12} color="#FFF" />
          </button>

          {mediaStore.mediaFiles.length > 1 && (
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="absolute bottom-[4px] right-[5px] bg-white p-[5px] rounded-md cursor-grab active:cursor-grabbing z-30"
              data-testid={`uploaded-file-drag-${index}`}
            >
              <Arrow />
            </button>
          )}
          {media.type.startsWith("video") && !isPlaying && (
            <button
              onClick={handlePlayClick}
              data-testid={`uploaded-file-play-${index}`}
            >
              <Play />
            </button>
          )}

          {index === 0 && mediaStore.mediaFiles.length > 1 && (
            <CustomBadge
              variant="outline"
              className="absolute bottom-[4px] left-[5px] text-xs!"
              data-testid="uploaded-file-primary-badge"
            >
              Primary
            </CustomBadge>
          )}
        </div>
      </>
    );
  }
);

export default UploadedFiles;
